/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import { parseScriptTags } from '@/lib/tracking/parse-script-tags';

/**
 * Injects a raw HTML snippet's `<script>` tags into `getContainer()` via
 * imperative DOM APIs, on mount — the Storefront Next equivalent of the
 * legacy `<isprint value="${...}" encoding="off" />` includes, adapted to
 * work around two React 19 JSX `<script>` behaviors that make a plain
 * `<script {...attrs} dangerouslySetInnerHTML>` unsuitable for arbitrary
 * third-party snippets like Adobe Launch/Omniture's:
 *
 * 1. React treats any JSX `<script async src="...">` as a hoistable
 *    "Resource": it gets moved into `<head>` and deduplicated by `src`,
 *    regardless of where it's rendered in the tree — silently breaking a
 *    *footer* include that's supposed to run after the rest of the page.
 * 2. React treats a lowercase `onload`/`onerror`/etc. prop as an attempted
 *    (invalid) synthetic event handler and drops it entirely, rather than
 *    passing it through as a literal HTML attribute — breaking snippets
 *    like `<script src="..." onload="triggerFooterCall()">` where the
 *    inline handler is load-bearing.
 *
 * Creating the elements with `document.createElement('script')` and
 * `setAttribute` bypasses both special cases entirely (this is the same
 * technique third-party loader snippets — e.g. GTM's own async loader —
 * use to self-inject), while still executing normally once appended,
 * per the standard DOM behavior for scripts inserted via script rather
 * than parsed by the HTML parser.
 *
 * Client-only by nature (scripts are created in an effect, after mount) —
 * unlike OneTrustLoader/PinterestDomainVerification, this snippet is not
 * present in the initial server-rendered HTML.
 */
export function useRawScriptInclude(
    raw: string | null | undefined,
    nonce: string | null | undefined,
    getContainer: () => HTMLElement | null
) {
    useEffect(() => {
        if (!raw) {
            return undefined;
        }

        const container = getContainer();
        if (!container) {
            return undefined;
        }

        const inserted = parseScriptTags(raw).map(({ attrs, content }) => {
            const script = document.createElement('script');
            Object.entries(attrs).forEach(([name, value]) => script.setAttribute(name, value));
            if (nonce) {
                // Assigned as a property (not setAttribute) — browsers hide the nonce
                // content attribute after an element is created, but still honor the
                // IDL property for CSP matching. This is the documented-safe way to
                // set a nonce on a script created and inserted via script.
                script.nonce = nonce;
            }
            if (content) {
                script.text = content;
            }
            container.appendChild(script);
            return script;
        });

        return () => {
            inserted.forEach((script) => script.remove());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- getContainer resolves a stable global (document.head/body); only `raw`/`nonce` should re-trigger injection.
    }, [raw, nonce]);
}
